// Only reveal the loading overlay if the shell page transition takes longer
// than this. Once the shell app itself is already loaded, switching between
// sncf pages can render almost instantly; showing (and immediately hiding) the
// overlay in that case causes a flash where new content briefly shows, gets
// covered by the fading-in grey overlay, then reappears once it fades back out.
const SHOW_DELAY_MS = 200;

// Once shown, keep the overlay visible at least this long so it's never a
// barely-perceptible flicker.
const MIN_VISIBLE_MS = 500;

// Hard cap: never leave the overlay stuck if the page never finishes rendering
// (e.g. `frontendShell.execute()` can take a while on the very first navigation
// into the sncf flow, while it loads the whole shell app bundle).
const MAX_WAIT_FOR_RENDER_MS = 8000;

// Hard cap for `hideDirectLinkWaitingPage`: never leave the waiting page shown
// forever if the shell never renders into the watched container.
const MAX_WAIT_FOR_DIRECT_LINK_RENDER_MS = 8000;

module.exports = {
    /**
     * Hides the synchronous "waiting_intermediate_page" overlay that the JSP shows
     * inline (before any JS has loaded) for a direct link/refresh into an sncf
     * route. Unlike `showLoadingIfSlow`, the overlay here was already shown
     * server-side (outside of `enhanceLoading`'s own state tracking), so we only
     * need to hide it once the shell has rendered real content into `containerId`,
     * via an independent MutationObserver, without touching `enhanceLoading`'s
     * "shown" internal state (calling `hideLoading()` directly is safe either way).
     * @param {Object} $waitingPage jQuery wrapped waiting page element
     * @param {string} containerId DOM id of the shell portal container (without '#')
    */
    hideDirectLinkWaitingPage($waitingPage, containerId) {
        const container = document.getElementById(containerId);
        let settled = false;

        const hideNow = () => {
            if (settled) {
                return;
            }
            settled = true;
            if (observer) {
                observer.disconnect();
            }
            Odigeo.UIElements.enhanceLoading($waitingPage).hideLoading();
        };

        let observer;
        if (container) {
            observer = new MutationObserver(() => {
                hideNow();
            });
            observer.observe(container, {childList: true, subtree: true});
        }
        setTimeout(hideNow, MAX_WAIT_FOR_DIRECT_LINK_RENDER_MS);
    },

    /**
     * Shows the loading overlay while the shell portal is empty and hides it
     * as soon as the shell renders content. If the portal is already rendered,
     * this is a no-op, which avoids showing the overlay again when navigating
     * back to an already visited page.
     * @param {Object} $waitingPage jQuery wrapped waiting page element
     * @param {Array<string>} labels Labels to pass to `showLoading`
     * @param {string} containerId DOM id of the shell portal container (without '#')
     */
    showLoadingWhileEmpty($waitingPage, labels, containerId) {
        const container = document.getElementById(containerId);
        if (!container) {
            return;
        }

        const hasContent = () => container.firstChild !== null;
        if (hasContent()) {
            return;
        }

        let settled = false;
        let observer;
        const hideNow = () => {
            if (settled) {
                return;
            }
            settled = true;
            observer.disconnect();
            Odigeo.UIElements.enhanceLoading($waitingPage).hideLoading();
        };

        Odigeo.UIElements.enhanceLoading($waitingPage).showLoading(labels);

        observer = new MutationObserver(() => {
            if (!hasContent()) {
                return;
            }
            hideNow();
        });
        observer.observe(container, {childList: true});
        setTimeout(hideNow, MAX_WAIT_FOR_RENDER_MS);
    },

    /**
     * Watches `containerId` for the DOM mutation caused by an upcoming shell
     * page navigation and only reveals the loading overlay if that transition
     * hasn't completed within `SHOW_DELAY_MS`. Call this once, synchronously,
     * before triggering the navigation (e.g. before `frontendShell.execute`).
     * @param {Object} $waitingPage jQuery wrapped waiting page element
     * @param {Array<string>} labels Labels to pass to `showLoading`
     * @param {string} containerId DOM id of the shell portal container (without '#')
    */
    showLoadingIfSlow($waitingPage, labels, containerId) {
        const container = document.getElementById(containerId);
        let shown = false;
        let settled = false;
        let shownAt = null;

        const hideNow = () => {
            if (settled) {
                return;
            }
            settled = true;
            if (shown) {
                Odigeo.UIElements.enhanceLoading($waitingPage).hideLoading();
            }
        };

        const settleRespectingMinimum = () => {
            if (!shown) {
                settled = true;
                return;
            }
            const remaining = MIN_VISIBLE_MS - (Date.now() - shownAt);
            if (remaining > 0) {
                setTimeout(hideNow, remaining);
            } else {
                hideNow();
            }
        };

        const showTimer = setTimeout(() => {
            if (settled) {
                return;
            }
            shown = true;
            shownAt = Date.now();
            Odigeo.UIElements.enhanceLoading($waitingPage).showLoading(labels);
        }, SHOW_DELAY_MS);

        let observer;
        if (container) {
            observer = new MutationObserver(() => {
                if (settled) {
                    return;
                }
                clearTimeout(showTimer);
                observer.disconnect();
                settleRespectingMinimum();
            });
            observer.observe(container, {childList: true, subtree: true, attributes: true, characterData: true});
        }

        setTimeout(() => {
            if (settled) {
                return;
            }
            clearTimeout(showTimer);
            if (observer) {
                observer.disconnect();
            }
            hideNow();
        }, MAX_WAIT_FOR_RENDER_MS);
    },
};
